import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomers } from '../../hooks/useCustomers';
import { InvoiceRequest, ProformaRequest, InvoiceItem } from '../../types/billing';
import { billingService } from '../../services/billingService';
import { saveDraftInvoice, saveDraftProforma } from '../../lib/services/billingService';
import InvoiceForm from './InvoiceForm';
import Modal from '../common/Modal';
import { DocumentTextIcon, PaperAirplaneIcon, DocumentPlusIcon } from '@heroicons/react/24/outline';

/** Forrásbizonylat, amiből számla készül (munkalap / megrendelés / szállítólevél / árajánlat). */
export interface InvoiceSource {
  type: 'worksheet' | 'order' | 'delivery_note' | 'quotation';
  id: string;
  number?: string;
  customerId?: string;
  items: InvoiceItem[];
  notes?: string;
  currency?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  source: InvoiceSource | null;
  documentType?: 'INVOICE' | 'PROFORMA';
  /** kiállítás / vázlat után (pl. lista frissítés) */
  onDone?: (result: { mode: 'draft' | 'issued'; invoiceNumber?: string }) => void;
}

/** Sortételek egységes konvertálása számlatételre. */
export const toInvoiceItems = (rows: any[]): InvoiceItem[] =>
  (rows || []).filter(r => r && r.type !== 'header').map(r => {
    const quantity = Number(r.deliveredQuantity ?? r.quantity ?? 1) || 1;
    const unitPrice = Number(r.unitPrice ?? r.price ?? 0) || 0;
    const discount = Number(r.discount ?? 0) || 0;
    const taxRate = Number(r.taxRate ?? r.vatRate ?? 27);
    const effUnit = Math.round(unitPrice * (1 - discount / 100) * 100) / 100;
    const net = Math.round(quantity * effUnit * 100) / 100;
    return { name: r.productName || r.name || 'Tétel', quantity, unit: r.unit || 'db', unitPrice: effUnit, taxRate, netValue: net, taxValue: Math.round(net * taxRate) / 100, grossValue: Math.round(net * (1 + taxRate / 100) * 100) / 100, comment: r.productCode ? `Cikkszám: ${r.productCode}` : (r.notes || '') };
  });

const SOURCE_LABEL: Record<InvoiceSource['type'], string> = { worksheet: 'Munkalap', order: 'Megrendelés', delivery_note: 'Szállítólevél', quotation: 'Árajánlat' };

const CreateInvoiceFromSource: React.FC<Props> = ({ isOpen, onClose, source, documentType = 'INVOICE', onDone }) => {
  const { user } = useAuth();
  const { customers } = useCustomers();
  const [pending, setPending] = useState<InvoiceRequest | ProformaRequest | null>(null);
  const [busy, setBusy] = useState(false);

  const initialData = useMemo(() => {
    if (!source) return undefined;
    const c = customers.find(x => x.id === source.customerId);
    const primary = c?.contactPersons?.find(p => p.isPrimary) || c?.contactPerson;
    return {
      buyerName: c ? (c.type === 'organization' ? c.companyName : [c.lastName, c.firstName].filter(Boolean).join(' ')) : '',
      buyerEmail: c?.email || primary?.email || '',
      buyerPhone: c?.phone || primary?.phone || '',
      buyerTaxNumber: c?.taxNumber || '',
      buyerAddress: c?.address || undefined,
      selectedCustomer: c || null,
      items: source.items,
      currency: source.currency || 'HUF',
      notes: source.notes || (source.number ? `${SOURCE_LABEL[source.type]}: ${source.number}` : ''),
      customerId: source.customerId,
      sourceType: source.type, sourceId: source.id, sourceNumber: source.number
    };
  }, [source, customers]);

  const withSource = (d: InvoiceRequest | ProformaRequest): InvoiceRequest | ProformaRequest => ({
    ...d, organizationId: user?.organizationId || '',
    customerId: source?.customerId, sourceType: source?.type, sourceId: source?.id, sourceNumber: source?.number,
    ...(source?.number ? { orderNumber: source.number } : {})
  });

  const saveAsDraft = async () => {
    if (!pending || !user?.organizationId) return;
    try {
      setBusy(true);
      if (documentType === 'PROFORMA') await saveDraftProforma(pending as ProformaRequest, user.organizationId);
      else await saveDraftInvoice(pending as InvoiceRequest, user.organizationId);
      toast.success('Vázlat elmentve – a Számlázás oldalon kiállítható');
      setPending(null); onClose(); onDone?.({ mode: 'draft' });
    } catch (e: any) { toast.error(e?.message || 'A vázlat mentése nem sikerült'); } finally { setBusy(false); }
  };

  const issueNow = async () => {
    if (!pending || !user?.organizationId) return;
    try {
      setBusy(true);
      const res = documentType === 'PROFORMA'
        ? await billingService.createProforma(pending as ProformaRequest, user.organizationId)
        : await billingService.createInvoice(pending as InvoiceRequest, user.organizationId);
      if (!res?.success) throw new Error(res?.error || 'A kiállítás nem sikerült');
      toast.success(`${documentType === 'PROFORMA' ? 'Díjbekérő' : 'Számla'} kiállítva: ${res.invoiceNumber}`);
      if (res.pdfBase64) {
        try {
          const bytes = Uint8Array.from(atob(res.pdfBase64), ch => ch.charCodeAt(0));
          const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
          window.open(url, '_blank');
        } catch { /* nem kritikus */ }
      }
      setPending(null); onClose(); onDone?.({ mode: 'issued', invoiceNumber: res.invoiceNumber });
    } catch (e: any) { toast.error(e?.message?.replace(/^.*?:\s*/, '') || 'A kiállítás nem sikerült'); } finally { setBusy(false); }
  };

  if (!source) return null;
  return (
    <>
      <InvoiceForm
        isOpen={isOpen && !pending}
        onClose={onClose}
        documentType={documentType}
        initialData={initialData}
        loading={busy}
        onSubmit={(d) => setPending(withSource(d))}
      />
      <Modal isOpen={!!pending} onClose={() => setPending(null)} title={documentType === 'PROFORMA' ? 'Díjbekérő' : 'Számla'} size="md">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0"><DocumentTextIcon className="w-5 h-5" /></div>
            <div className="text-sm">
              <div className="font-medium text-gray-900 dark:text-gray-100">{pending?.buyer?.name}</div>
              <div className="text-gray-500 dark:text-gray-400">{pending?.items?.length} tétel · {SOURCE_LABEL[source.type]}{source.number ? ` ${source.number}` : ''}</div>
              <div className="text-gray-500 dark:text-gray-400 mt-1">Bruttó: <b className="text-gray-900 dark:text-gray-100">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: pending?.currency || 'HUF', maximumFractionDigits: 0 }).format((pending?.items || []).reduce((s, i) => s + (i.grossValue ?? i.quantity * i.unitPrice * (1 + i.taxRate / 100)), 0))}</b></div>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">A kiállítás a Beállítások → Számlázás alatt megadott szolgáltatónál (Számlázz.hu / Billingo) történik, és a vevő e-mailben megkapja a bizonylatot. A vázlat később is kiállítható.</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-1">
            <button type="button" onClick={() => setPending(null)} className="crm-btn crm-btn-ghost sm:mr-auto" disabled={busy}>Vissza</button>
            <button type="button" onClick={saveAsDraft} className="crm-btn crm-btn-secondary" disabled={busy}><DocumentPlusIcon className="w-4 h-4" />Mentés vázlatként</button>
            <button type="button" onClick={issueNow} className="crm-btn crm-btn-primary" disabled={busy}><PaperAirplaneIcon className="w-4 h-4" />{busy ? 'Kiállítás…' : 'Kiállítás most'}</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateInvoiceFromSource;
